import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  structuredData?: object;
}

const SEO = ({
  title = "NearLab Services | Mobile DNA & DOT Testing",
  description = "Professional, Secure, and On-Site DNA & DOT Testing Delivered Where You Are. AABB-accredited lab services across Texas.",
  keywords = "DNA testing, paternity testing, DOT testing, mobile testing, Texas, Houston, AABB accredited, court admissible",
  image = "https://lovable.dev/opengraph-image-p98pqg.png",
  url = "https://nearlabservices.com",
  type = "website",
  structuredData
}: SEOProps) => {
  const baseStructuredData = {
    "@context": "https://schema.org",
    "@type": "MedicalBusiness",
    "name": "NearLab Services",
    "description": "Professional mobile DNA and DOT testing services",
    "url": "https://nearlabservices.com",
    "telephone": "+17135104442",
    "email": "info@nearlabservices.com",
    "address": {
      "@type": "PostalAddress",
      "addressRegion": "TX",
      "addressCountry": "US"
    },
    "serviceArea": {
      "@type": "State",
      "name": "Texas"
    },
    "medicalSpecialty": [
      "DNA Testing",
      "Paternity Testing", 
      "DOT Physical Examinations",
      "Drug Testing"
    ],
    "accreditation": "AABB Accredited",
    "priceRange": "$$",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Testing Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Legal Paternity Testing",
            "description": "Court-admissible paternity testing with proper chain of custody"
          }
        },
        {
          "@type": "Offer", 
          "itemOffered": {
            "@type": "Service",
            "name": "DOT Physical Examination",
            "description": "Department of Transportation medical examinations"
          }
        }
      ]
    }
  };

  const finalStructuredData = structuredData || baseStructuredData;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="NearLab Services" />
      <meta name="robots" content="index, follow" />
      <meta name="language" content="English" />
      <meta name="geo.region" content="US-TX" />
      <meta name="geo.placename" content="Texas" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={url} />
      
      {/* Open Graph Tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="NearLab Services" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      
      {/* Mobile Optimization */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="format-detection" content="telephone=yes" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(finalStructuredData)}
      </script>
    </Helmet>
  );
};

export default SEO;